import { useState, useEffect } from "react";
import {
  unregisterBook, fetchDescription,
  setGenre, setRecommended, setCompleted, setRegisteredDate, startReading, setWantToRead,
} from "../api/books";
import { formatDate, getUserDisplayName, getBookRegisteredDate } from "../utils/format";
import DateField from "../components/DateField";
import { getEmotionTag } from "../utils/firestoreHelpers";
import { useBookPage, bookPageUrl } from "../utils/bookPage";
import { GENRE_LIST } from "../data/constants";


const BookHero = ({ book, bookId, isOwned, isMyBook, currentUser, viewAsUserId, users, onBookUpdated, onBookCompleted, onBookRemoved, onMemoAdded, onAddToMine, onBookClick, onBack, onGoHome }) => {
  const [showGenrePicker, setShowGenrePicker] = useState(false);
  const [descOpen, setDescOpen] = useState(false);
  /* 삭제는 window.confirm 으로 물었다. 홈 화면에 추가한 앱에서는 그 창이
     뜨지 않는 경우가 있어, 눌러도 아무 일도 안 일어나는 것처럼 보였다.
     실패해도 조용히 지나갔다 — 화면 안에서 묻고, 안 되면 왜 안 됐는지
     보여준다. */
  const [confirmDel, setConfirmDel] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [delErr, setDelErr] = useState(null);
  /* 이 책에 공개 페이지가 있는가. 목록을 받아오면 다시 그려진다. */
  const hasPage = useBookPage(book.isbn);


  const uid = currentUser?.uid;

  // 소개문이 없는 책이면 여기서 한 번 채운다.
  // 게스트는 books 를 못 쓰므로(RLS) 시도하지 않는다.
  useEffect(() => {
    if (book?.description != null || currentUser?.isAnonymous || !uid) return;
    let alive = true;
    fetchDescription(book).then((d) => {
      // 다 쓴 화면에 결과가 늦게 도착해도 갱신을 부르지 않는다
      if (alive && d && onBookUpdated) onBookUpdated();
    });
    return () => { alive = false; };
  }, [book?.id, book?.description, currentUser?.isAnonymous, uid]); // eslint-disable-line react-hooks/exhaustive-deps

  /* 남의 기록을 보는 중인가. viewAsUserId 에 내 id 가 담겨 오는 길도 있어
     (!viewAsUserId) 로만 가르면 내 책에서 장르·추천·삭제가 통째로 사라진다.
     아래 날짜 칸은 이미 이렇게 갈라 보고 있었다 — 같은 기준으로 맞춘다. */
  const viewingOther = !!viewAsUserId && viewAsUserId !== "me" && viewAsUserId !== uid;

  const canEditGenre = isOwned && !viewingOther;

  return (
    <>
      <div className="book-hero-cover" style={{ background: book.bg, overflow:"hidden", padding:0 }}>
        {book.cover
          ? <img src={book.cover} alt={book.title} style={{width:"100%",height:"100%",objectFit:"contain"}} />
          : book.emoji || "📖"
        }
      </div>
      <div className="book-hero-info">
        <h2>{book.title}</h2>
        <div className="book-hero-author">{book.author}</div>
        <div className="book-tags">
          <span className={`tag tag-genre${canEditGenre ? " tag-genre-editable genre-pulse" : ""}`} onClick={() => { if (canEditGenre) setShowGenrePicker(!showGenrePicker); }}>{book.genre}{canEditGenre && " ▾"}</span>
          {showGenrePicker && canEditGenre && (
            <div className="genre-picker-overlay" onClick={() => setShowGenrePicker(false)}>
              <div className="genre-picker" onClick={e => e.stopPropagation()}>
                <div className="genre-picker-title">장르 변경</div>
                <div className="genre-picker-grid">
                  {[...GENRE_LIST, "기타"].map(g => (
                    <button key={g} className={`genre-option${book.genre === g ? " active" : ""}`} onClick={async () => {
                      await setGenre(book.id, g);
                      if (onBookUpdated) onBookUpdated();
                      setShowGenrePicker(false);
                    }}>{g}</button>
                  ))}
                </div>
              </div>
            </div>
          )}
          {(() => {
            const et = getEmotionTag(book);
            if (!et) return null;
            return <span className="tag tag-emotion">{et.emoji} {et.label}</span>;
          })()}
        </div>
        {/* 출판사 책 소개.
            '줄거리' 라고 쓰지 않는다. 실제로 오는 글은 작가 이력과 수상 실적이
            먼저 나오는 홍보 문구라, 줄거리라고 하면 기대와 어긋난다. */}
        {book.description && (
          <div className="book-desc">
            <button
              className="book-desc-toggle"
              aria-expanded={descOpen}
              onClick={() => setDescOpen((v) => !v)}
            >
              출판사 책 소개 <span className="book-desc-caret">{descOpen ? "▴" : "▾"}</span>
            </button>
            {descOpen && <p className="book-desc-body">{book.description}</p>}
          </div>
        )}
        {/* 추천 토글 */}
        {(() => {
          const recommended = book.recommendedBy || {};
          const viewUser = viewAsUserId && viewAsUserId !== "me" && viewAsUserId !== uid
            ? (users || []).find(u => u.id === viewAsUserId) : null;
          const targetId = viewUser ? viewUser.id : uid;
          const isRecommended = targetId && recommended[targetId];
          if (isOwned && !viewingOther && !currentUser?.isAnonymous) {
            return (
              <button
                className={`btn-recommend${isRecommended ? " active" : ""}`}
                onClick={async () => {
                  await setRecommended(book.id, uid, !isRecommended);
                  if (onBookUpdated) onBookUpdated();
                }}
              >
                {isRecommended ? "☀️ 추천 중 · 해제하기" : "이 책 추천하기 ☀️"}
              </button>
            );
          }
          if (isRecommended) {
            return <div className="recommend-badge-display">☀️ {viewUser ? viewUser.name : ""}님의 추천 책</div>;
          }
          return null;
        })()}
        {/* 누르는 순간 '읽는 중' 으로 등록됐다. 읽고 싶어 담아두려면 길이
            없어서, 등록하고 다시 '아직 안 읽었어요' 로 되돌려야 했다.
            책 등록 화면을 그대로 띄운다 — 이 책이 이미 골라진 채로
            상태(읽고 싶어요 / 읽는 중 / 다 읽었어요)와 장르만 정한다. */}
        {!isMyBook && !currentUser?.isAnonymous && (
          <button className="btn-add-to-mine" onClick={() => onAddToMine && onAddToMine(book)}>
            🪐 내 행성에 추가하기
          </button>
        )}
        {/* 이 책의 공개 페이지(muroo.kr/book/<isbn>)를 밖으로 보낸다.
            페이지가 있는 책에만 낸다 — 없는 주소는 404 가 아니라 앱 첫 화면이
            떠서, 받은 사람이 책 대신 로그인 화면을 본다.
            아직 목록을 못 받았으면(null) 아무것도 내지 않는다. */}
        {/* 공유 창과 복사를 쓰다가 그냥 여는 것으로 바꿨다. 복사는 권한을
            주지 않는 환경이 있어 실패했고(확인 중에 겪었다), 공유 창은 기기마다
            있다 없다 한다. 브라우저로 열면 주소창에 주소가 그대로 보여서
            복사하든 공유하든 사용자가 알아서 한다. */}
        {book.isbn && hasPage && (
          <a
            className="btn-share-page"
            href={bookPageUrl(book.isbn)}
            target="_blank"
            rel="noopener noreferrer"
          >
            🔗 이 책 페이지 열기
          </a>
        )}
        {(isMyBook || viewAsUserId) && (() => {
          const myName = getUserDisplayName(currentUser);
          const viewUser = viewAsUserId && viewAsUserId !== "me" && viewAsUserId !== uid
            ? (users || []).find(u => u.id === viewAsUserId) : null;
          const targetId = viewUser ? viewUser.id : uid;
          const targetName = viewUser ? viewUser.name : myName;
          const targetCompleted = targetId && book.completedBy ? book.completedBy[targetId] : null;
          const displayName = targetName;
          const displayCompleted = targetCompleted;
          const canEdit = isOwned && !viewUser;

          const targetRegisteredDate = getBookRegisteredDate(book, targetId);

          const saveStart = async (dateStr) => {
            await setRegisteredDate(book.id, uid, dateStr);
            if (onMemoAdded) onMemoAdded(book.id, null);
          };
          const saveComplete = async (dateStr) => {
            await setCompleted(book.id, uid, dateStr);
            if (onBookCompleted) onBookCompleted(book.id, uid, dateStr);
          };
          const clearComplete = async () => {
            await setCompleted(book.id, uid, null);
            if (onBookCompleted) onBookCompleted(book.id, uid, null);
          };

          /* 아직 펴지 않은 책에 시작일·완료일을 물으면 안 된다.
             담아둔 상태임을 밝히고, 펴는 길만 하나 준다. */
          const targetWish = targetId && book.wantToReadBy ? !!book.wantToReadBy[targetId] : false;
          if (targetWish) {
            return (
              <div className="book-dates book-wish">
                <div className="book-date-who">{displayName}</div>
                <div className="book-wish-row">
                  <span className="book-wish-label">🔖 읽고 싶어 담아둔 책</span>
                  {canEdit && (
                    <button className="btn-start-reading" onClick={async (e) => {
                      e.stopPropagation();
                      await startReading(book.id, uid);
                      if (onMemoAdded) onMemoAdded(book.id, null);
                    }}>읽기 시작하기</button>
                  )}
                </div>
              </div>
            );
          }

          return (
            <div className="book-dates">
              <div className="book-date-who">{displayName}</div>
              <div className="book-date-row">
                <DateField
                  label="📖 시작일"
                  value={targetRegisteredDate}
                  canEdit={canEdit}
                  onSave={saveStart}
                />
                {displayCompleted || !canEdit ? (
                  <DateField
                    label="✅ 완료일"
                    value={displayCompleted}
                    canEdit={canEdit}
                    completed={!!displayCompleted}
                    onSave={saveComplete}
                    onClear={clearComplete}
                    /* 완료일이 시작일보다 앞서는 것을 막는다 */
                    min={targetRegisteredDate}
                    emptyText="읽는 중..."
                  />
                ) : (
                  <div className="book-date-item">
                    <span className="book-date-label">✅ 완료일</span>
                    <button className="btn-complete" onClick={async (e) => {
                      e.stopPropagation();
                      await saveComplete(formatDate(new Date()));
                    }}>독서 완료</button>
                  </div>
                )}
              </div>
              {/* 잘못 눌러 들어온 길을 되돌린다. 완독한 책에는 내놓지 않는다 —
                  다 읽은 책을 '아직 안 읽음' 으로 보내는 건 되돌리기가 아니라
                  기록을 지우는 일이다.
                  기록이 남아 있으면 한 번 묻는다. 시작일이 오늘로 다시
                  잡히면서 며칠째 읽었는지가 사라지기 때문이다. */}
              {canEdit && !displayCompleted && (
                <button
                  className="btn-back-to-wish"
                  onClick={async (e) => {
                    e.stopPropagation();
                    const myMemos = (book.memos || []).filter((m) => m.userId === uid).length;
                    if (myMemos > 0 &&
                        !window.confirm(`이 책에 남긴 기록이 ${myMemos}개 있어요. 담아둔 책으로 되돌리면 시작일이 사라집니다. 그래도 되돌릴까요?`)) return;
                    await setWantToRead(book.id, uid, true);
                    if (onMemoAdded) onMemoAdded(book.id, null);
                  }}
                >🔖 아직 안 읽었어요</button>
              )}
            </div>
          );
        })()}
        {isOwned && !viewingOther && (
          <div className="book-remove">
            {!confirmDel ? (
              <button className="btn-remove-book" onClick={() => setConfirmDel(true)}>
                내 서재에서 삭제
              </button>
            ) : (
              <div className="book-remove-ask">
                <span className="book-remove-q">내 서재에서 지울까요?</span>
                <button
                  className="btn-remove-yes"
                  disabled={removing}
                  onClick={async () => {
                    setRemoving(true);
                    setDelErr(null);
                    try {
                      // user_books 행 하나만 지우면 된다.
                      // 완료일/시작일은 그 행에 있었고, 아무도 안 가진 책은
                      // 트리거가 지운다.
                      await unregisterBook(book.id, uid);
                      if (onBookRemoved) onBookRemoved(book.id);
                      /* 뒤로 가면 이 책을 어디서 왔든 그 목록으로 돌아간다 —
                         방금 지운 책이 아직 있는 것처럼 보이거나, 남의
                         행성이면 지운 것과 상관없는 자리다. 내 홈으로 나간다. */
                      if (onGoHome) onGoHome();
                      else onBack();
                    } catch (e) {
                      setDelErr(e?.message || "알 수 없는 이유");
                      setRemoving(false);
                    }
                  }}
                >{removing ? "지우는 중…" : "지우기"}</button>
                <button
                  className="btn-remove-no"
                  onClick={() => { setConfirmDel(false); setDelErr(null); }}
                >취소</button>
              </div>
            )}
            {delErr && <div className="book-remove-err">지우지 못했어요 · {delErr}</div>}
          </div>
        )}
      </div>
    </>
  );
};

export default BookHero;
